// @flow
import context from 'app/common/lib/context';
import createImageStopwatch from 'app/common/lib/TraceLogger/gridHandler/createImageStopwatch';
import DomUtil from 'app/common/lib/DomUtil';
import getFilename from 'app/packages/trace-logger/utils/getFilename';
import getGridItemImageUrls from 'app/common/lib/TraceLogger/gridHandler/getGridItemImageUrls';
import getImagesInViewport from 'app/common/lib/TraceLogger/gridHandler/getImagesInViewport';
import longPollImageTimings from 'app/common/lib/TraceLogger/gridHandler/longPollImageTimings';
import Stopwatch from 'app/packages/trace-logger/Stopwatch';
import type {
  GridHandlerOptions,
  GridHandler,
  GridItemType,
} from 'app/common/lib/TraceLogger/gridHandler/types';
import type {
  RegisterGridImagesTraceEvent,
  RegisterGridItemsPositionedTraceEvent,
  CustomTraceEvent,
  StopTraceEvent,
} from 'app/common/lib/TraceLogger/types';

const DEFAULT_STOP_TIMEOUT = 120000; // 120 second timeout default

type State =
  | {|
      type: 'registering',
      viewportFilled: boolean,
      urlToGridItemTypeMap: { [url: string]: GridItemType },
    |}
  | {|
      type: 'positioned',
      lastSubspanEnd: null | number | 'timeout',
    |}
  | {| type: 'ended' |};

export default function experimentalGridHandler({
  timerExists,
  pageLoad,
  constraintsDidEnd,
}: GridHandlerOptions): GridHandler {
  let timingStopped: boolean = false;
  let state: State = {
    type: 'registering',
    viewportFilled: false,
    urlToGridItemTypeMap: {},
  };

  const done = (lastSubspanEnd: number | 'timeout') => {
    if (state.type === 'ended') {
      return;
    }
    state = { type: 'ended' };
    constraintsDidEnd(lastSubspanEnd);
  };

  /**
   * This handler kicks off the logic that determines which images are in the first page load
   * and registers stopwatches for each one as submetrics of the current timer.
   *
   * This is applicable to both user nav and page load timings that load grid images.
   */
  const handleRegisterGridImages = (event: RegisterGridImagesTraceEvent) => {
    if (
      state.type !== 'registering' ||
      state.viewportFilled ||
      (event.el && !DomUtil.isAnyPartOfElementVisibleInViewport(event.el))
    ) {
      return;
    }

    // If the viewport is filled, we're no longer registering images
    if (event.viewportFilled) {
      state.viewportFilled = true;
    }

    const devicePixelRatio = window.devicePixelRatio;
    const isRetina = context.instance.is_retina;

    // Takes grid data (props.items) from the Masonry grid and stores it for later reconciliation
    // Stores registered images in an array of image filenames to be reconciled later
    const urlToTypeMap = {};
    event.gridData
      .filter((item) => ['pin', 'board', 'interest'].includes(item.type))
      .forEach((item) => {
        getGridItemImageUrls({ item, devicePixelRatio, isRetina })
          .map((url) => getFilename(url))
          .filter(Boolean)
          .forEach((filename) => {
            urlToTypeMap[filename] = item.type;
          });
      });

    state.urlToGridItemTypeMap = { ...state.urlToGridItemTypeMap, ...urlToTypeMap };
  };

  /**
   * At this point we know that all the grid items have been registered, normalized, and positioned.
   * We can now extract image data and populate substopwatches for them.
   *
   * For page loads, we use the window's `onLoad` event to signal that the grid items in
   * Masonry have been positioned (see TraceLogger/utils.js). There may be a better way
   * to tell this in the future.
   *
   * NOTE: While it seems like this is always called before the stop timer event, the stop timer
   * event can actually called independently of this event (for abort reasons, etc) so they can't
   * be collapsed.
   */
  const handleGridItemsPositioned = (
    event: RegisterGridItemsPositionedTraceEvent,
    timer: Stopwatch,
  ) => {
    if (state.type !== 'registering') {
      return;
    }
    timer.addAnnotation(event.name);
    const { urlToGridItemTypeMap } = state;
    state = { type: 'positioned', lastSubspanEnd: null };
    getImagesInViewport(urlToGridItemTypeMap)
      .then((imageUrls) =>
        longPollImageTimings({
          imageUrls,
          shouldAbort: () => state.type === 'ended' || !timerExists(),
        }),
      )
      .then((urlToTimingMap) =>
        Object.keys(urlToTimingMap).map((url) =>
          createImageStopwatch({
            metricName: timer.metricName,
            coexistId: url,
            resourceTiming: urlToTimingMap[url],
          }),
        ),
      )
      .then((imageStopwatches) => {
        imageStopwatches.forEach((imageStopwatch) => {
          timer.addSubStopwatch(imageStopwatch);
        });
        return imageStopwatches.map(({ endTime }) => endTime || 0);
      })
      .then((endTimes) => {
        if (state.type !== 'positioned') {
          return;
        }
        const lastSubspanEnd = endTimes.every(Boolean) // check if every subspan has eneded
          ? Math.max(0, ...endTimes) // if so, get the max end time
          : 'timeout';
        if (timingStopped) {
          done(lastSubspanEnd);
        } else {
          state.lastSubspanEnd = lastSubspanEnd;
        }
      });
  };

  return {
    dispatch: (event: CustomTraceEvent | StopTraceEvent, timer: Stopwatch) => {
      if (
        (pageLoad && event.name === 'PageLoadRegisterGridImagesTraceEvent') ||
        (!pageLoad && event.name === 'UserNavRegisterGridImagesTraceEvent')
      ) {
        handleRegisterGridImages(event);
        return true;
      }
      if (
        (pageLoad && event.name === 'PageLoadRegisterGridItemsPositionedTraceEvent') ||
        (!pageLoad && event.name === 'UserNavRegisterGridItemsPositionedTraceEvent')
      ) {
        handleGridItemsPositioned(event, timer);
        return true;
      }
      if (state.type !== 'ended' && !timingStopped && event.name === 'stop') {
        timingStopped = true;
        if (state.type === 'positioned' && state.lastSubspanEnd !== null) {
          done(state.lastSubspanEnd);
        } else {
          setTimeout(() => done('timeout'), DEFAULT_STOP_TIMEOUT);
        }
        return true;
      }
      return false;
    },
    cleanup: () => {
      timingStopped = false;
      state = {
        type: 'registering',
        viewportFilled: false,
        urlToGridItemTypeMap: {},
      };
    },
  };
}
